import type { TFunction } from 'i18next';
import type { NexaPurchase } from '@/lib/disruptions/types';

/** The reasons the console words (`operateCancel:need.why.purchase.<REASON>`); anything else → `other`. */
const REASONS: ReadonlySet<string> = new Set([
  'AUTO_PURCHASE_DISABLED',
  'POLICY_UNAVAILABLE',
  'PRICE_CARD_UNAVAILABLE',
  'OVER_PRICE_CAP',
  'PRICE_NOT_COMPARABLE',
  'MISSING_DATA',
  'PASSPORT_REQUIRED',
  'LIVE_BOOKING_DISABLED',
  'SPECIAL_ASSISTANCE',
  'PROVIDER_ERROR',
  'PURCHASE_FAILED',
  'PURCHASE_UNCONFIRMED',
  'TICKET_NUMBERS_PENDING',
  'SOLD_OUT_NO_OPTIONS',
  'ITINERARY_CHANGED',
  'PURCHASE_REFUSED',
]);

/** The traveler facts the console words (`operateCancel:need.missing.<field>`). */
const FIELDS: readonly string[] = [
  'name',
  'dateOfBirth',
  'dateOfBirthMismatch',
  'gender',
  'title',
  'email',
  'phone',
  'nationality',
  'passportNumber',
  'passportIssuingCountry',
  'passportExpiry',
  'responsibleAdult',
];

export interface IPurchaseWhy {
  /** `need.why.purchase.<key>`. */
  key: string;
  /** Distinct missing facts, in a stable order (`need.missing.<field>`). */
  fields: string[];
  /** How many passengers miss something. */
  pax: number;
  /** PRICE and cap in USD (whole), when the reason is about the cap. */
  price?: number;
  cap?: number;
  /** The vendor / domain code behind a provider reason. */
  code?: string;
}

/**
 * Why a flight Nexa buys sits in «Te necesitan» (owner decision 4, 2026-10-30) — exactly which
 * data is missing, which price over which cap, which vendor code. Pure: the card words it.
 */
export function purchaseWhy(p: NexaPurchase | undefined): IPurchaseWhy {
  const known = p?.reason && REASONS.has(p.reason) ? p.reason : 'other';
  // Over the policy's cap PER PASSENGER, or over a bulk run's cap for the whole option.
  const reason = known === 'OVER_PRICE_CAP' && p?.capBasis === 'PER_PASSENGER' ? 'OVER_PRICE_CAP_PER_PASSENGER' : known;
  const present = new Set((p?.missing ?? []).map((m) => m.field));
  const fields = FIELDS.filter((f) => present.has(f));
  const pax = new Set((p?.missing ?? []).map((m) => m.paxUrn ?? '-')).size;
  return {
    key: reason,
    fields,
    pax,
    ...(typeof p?.priceMinor === 'number' ? { price: Math.round(p.priceMinor / 100) } : {}),
    ...(typeof p?.capMinor === 'number' ? { cap: Math.round(p.capMinor / 100) } : {}),
    ...(p?.detail ? { code: p.detail } : {}),
  };
}

/** «Te necesitan» for a flight Nexa buys, in words: the reason, with exactly the data missing. */
export function purchaseWhyText(t: TFunction<'operateCancel'>, purchase: NexaPurchase | undefined): string {
  const w = purchaseWhy(purchase);
  const fields = w.fields.map((f) => t(`need.missing.${f}`)).join(', ');
  return t(`need.why.purchase.${w.key}`, {
    fields,
    count: w.pax,
    price: w.price ?? '—',
    cap: w.cap ?? '—',
    code: w.code ?? '—',
  });
}
